import React, { useContext, useEffect, useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import {
  Container,
  FlexboxCol,
  FlexboxRow,
  Heading,
  Section,
  Text,
  Link,
} from 'src/components/Base'
import Brand from 'src/assets/images/brand.inline.svg'
import FacebookIcon from 'src/assets/images/facebook.inline.svg'
import InstagramIcon from 'src/assets/images/instagram.inline.svg'
import LinkedinIcon from 'src/assets/images/linkedin.inline.svg'
import QualityAustriaLogo from 'src/assets/images/quality-austria.png'
import IqnetLogo from 'src/assets/images/iqnet.png'
import LanguageContext from 'src/context/LanguageContext'
import { v4 as uuid } from 'uuid'
import ReactHtmlParser from 'react-html-parser'
import {
  cAddress,
  cAddressElement,
  cBrandCol,
  cBrandWrapper,
  cCertificates,
  cCertificateIqnet,
  cCertificateQuality,
  cFooter,
  cFooterCol,
  cFooterHeader,
  cFooterMenuItem,
  cFooterMenuItemHorizontal,
  cFooterMenuLink,
  cFooterMenuList,
  cFooterMenuListHorizontal,
  cFooterRow,
  cFooterSection,
  cSocialBar,
  cSocialBarIcon,
  cSocialIcon,
  cSocialIconSvg,
  cSocket,
  cSocketMenu,
} from './styles.module.scss'

const Footer = () => {
  const data = useStaticQuery(graphql`
    query FooterMenuQuery {
      allWp {
        nodes {
          themeGeneralSettings {
            plakativMenu {
              fieldGroupName
              footerMenu1De {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
              footerMenu1En {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
              footerMenu2De {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
              footerMenu2En {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
              socketDe {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
              socketEn {
                fieldGroupName
                links {
                  fieldGroupName
                  link {
                    target
                    title
                    url
                  }
                }
              }
            }
            socialMedia {
              optionFacebookUrl
              optionInstagramUrl
              optionLinkedinUrl
            }
            websitenbetreiber {
              impressumAddress
              impressumCity
              impressumCompany
              impressumCountry
              impressumEmail
              impressumPhone
              impressumZip
            }
            plakativStringTranslations {
              stringTranslationFooterCol1
              stringTranslationFooterCol2
              stringTranslationFooterCol1En
              stringTranslationFooterCol2En
            }
          }
        }
      }
    }
  `)

  const { socialMedia, websitenbetreiber, plakativStringTranslations } =
    data.allWp.nodes[0].themeGeneralSettings

  const languageContext = useContext(LanguageContext)

  const {
    footerMenu1De,
    footerMenu1En,
    footerMenu2De,
    footerMenu2En,
    socketDe,
    socketEn,
  } = data?.allWp?.nodes[0]?.themeGeneralSettings?.plakativMenu

  const [footerMenuCol1, setFooterMenuCol1] = useState(footerMenu1De)
  const [footerMenuCol2, setFooterMenuCol2] = useState(footerMenu2De)
  const [socket, setSocket] = useState(socketDe)

  useEffect(() => {
    switch (languageContext.currentLanguage) {
      case 'en':
        setFooterMenuCol1(footerMenu1En)
        setFooterMenuCol2(footerMenu2En)
        setSocket(socketEn)
        break
      default:
        setFooterMenuCol1(footerMenu1De)
        setFooterMenuCol2(footerMenu2De)
        setSocket(socketDe)
    }
  }, [languageContext.currentLanguage])

  const renderMenu = (name, horizontal = false) => {
    const menuList = name?.links?.map((item) => {
      // return html to avoid &amp and other html entities
      const title = item?.link?.title

      return title ? (
        <li
          key={uuid()}
          className={horizontal ? cFooterMenuItemHorizontal : cFooterMenuItem}
        >
          <Link
            className={cFooterMenuLink}
            to={item.link.url}
            target={item.link.target}
          >
            {ReactHtmlParser(title)}
          </Link>
        </li>
      ) : null
    })

    return (
      <ul className={horizontal ? cFooterMenuListHorizontal : cFooterMenuList}>
        {menuList}
      </ul>
    )
  }

  return (
    <footer className={cFooter}>
      <Section className={cFooterSection}>
        <FlexboxRow className={cFooterRow}>
          <FlexboxCol className={cFooterCol} width={6}>
            <div className={cBrandWrapper}>
              <div className={cBrandCol}>
                <Brand />
                <ul className={cSocialBar}>
                  <li className={cSocialBarIcon}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cSocialIcon}
                      href={socialMedia.optionFacebookUrl}
                      title="Facebook Bellutti Group"
                    >
                      <FacebookIcon className={cSocialIconSvg} />
                    </a>
                  </li>
                  <li className={cSocialBarIcon}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cSocialIcon}
                      href={socialMedia.optionInstagramUrl}
                      title="Instagram Bellutti Group"
                    >
                      <InstagramIcon className={cSocialIconSvg} />
                    </a>
                  </li>
                  <li className={cSocialBarIcon}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cSocialIcon}
                      href={socialMedia.optionLinkedinUrl}
                      title="LinkedIn Bellutti Group"
                    >
                      <LinkedinIcon className={cSocialIconSvg} />
                    </a>
                  </li>
                </ul>
              </div>
              <div className={cCertificates}>
                <img
                  className={cCertificateQuality}
                  src={QualityAustriaLogo}
                  alt="Quality Austria – Systemzertifiziert ISO 9001:2015"
                  width="460"
                  height="243"
                  loading="lazy"
                />
                <img
                  className={cCertificateIqnet}
                  src={IqnetLogo}
                  alt="IQNet – Certified Management System"
                  width="840"
                  height="840"
                  loading="lazy"
                />
              </div>
            </div>
          </FlexboxCol>
          <FlexboxCol className={cFooterCol} width={3}>
            <Heading className={cFooterHeader} color="light" size={4} as="h3">
              {languageContext.currentLanguage === 'en'
                ? plakativStringTranslations.stringTranslationFooterCol1En
                : plakativStringTranslations.stringTranslationFooterCol1}
            </Heading>
            {renderMenu(footerMenuCol1, false)}
          </FlexboxCol>
          <FlexboxCol className={cFooterCol} width={3}>
            <Heading className={cFooterHeader} color="light" size={4} as="h3">
              {languageContext.currentLanguage === 'en'
                ? plakativStringTranslations.stringTranslationFooterCol2En
                : plakativStringTranslations.stringTranslationFooterCol2}
            </Heading>
            {renderMenu(footerMenuCol2, false)}
          </FlexboxCol>
        </FlexboxRow>
        <Container>
          <div className={cSocket}>
            <div className={cAddress}>
              <Text as="span" className={cAddressElement}>
                {websitenbetreiber.impressumCompany}
              </Text>
              <Text
                as="span"
                className={cAddressElement}
              >{`${websitenbetreiber.impressumAddress}, ${websitenbetreiber.impressumZip} ${websitenbetreiber.impressumCity}, ${websitenbetreiber.impressumCountry}`}</Text>
              <Text as="span" className={cAddressElement}>
                {websitenbetreiber.impressumPhone}
              </Text>
              <Text as="span" className={cAddressElement}>
                {websitenbetreiber.impressumEmail}
              </Text>
            </div>
            <nav className={cSocketMenu}>{renderMenu(socket, true)}</nav>
          </div>
        </Container>
      </Section>
    </footer>
  )
}

export default Footer
